import {
  validatePassword,
  strength as passwordStrength,
} from '@components-member/PasswordStrengthMeter/passwordStrength.validation.service';
import { FieldValue } from '@components-shared/forms/formField.interface';
import { keyMap } from '@locales/lokalise/keyMap';
import { REGEX_CONSTANTS } from 'components/shared/constants';
import moment, { MomentInput } from 'moment';
import type {
  DatetimeValidatorType,
  EmailValidatorType,
  Int32ValidatorType,
  OnlyEngAndDigitsValidatorType,
  OnlyEngLettersValidatorType,
  PhoneValidatorType,
  SelectValidatorType,
  ValidatorsType,
  ValidDateParams,
  ValidEmailParams,
  ValidParams,
  ValidSelectParams,
} from './interfaces';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH-mm-ss';

export const EMAIL_VALIDATION_PATTERN = /^([a-z0-9_+-]+\.)*[a-z0-9_+-]+@[a-z0-9_-]+(\.[a-z0-9_-]+)*\.[a-z]{2,6}$/;

export const EMAIL_LIST_VALIDATION_PATTERN =
  /^((([a-z0-9_+-]+\.)*[a-z0-9_+-]+@[a-z0-9_-]+(\.[a-z0-9_-]+)*\.[a-z]{2,6})(,\s*|$))+$/i;

const isValidEmail = (value: string, params: ValidEmailParams): boolean => {
  if (!params.required && !value) {
    return true;
  }
  return EMAIL_VALIDATION_PATTERN.test(value.toLowerCase());
};

const isValidDate = (value: MomentInput, params: ValidDateParams): boolean => {
  const { timeFormat } = params;
  const format = `${DATE_FORMAT}${timeFormat ? ` ${TIME_FORMAT}` : ''}`;
  const date = params.view && params.view === 'dropdown' ? moment(value) : moment(value, format, true);

  return value === '' || date.isValid();
};

const isValidSelect = (value: string | null, params: ValidSelectParams): boolean => {
  const { options: optionList, required, form } = params;
  let options = typeof optionList === 'function' ? optionList(form) : optionList;
  const formattedValue = value !== null ? value : '';

  options = options || [];
  const valueIdx = options.findIndex((o) => o.value.toString() === formattedValue.toString());

  return !required || valueIdx !== -1;
};

const isValid = (value: string, types: string[], params: ValidParams, regexPattern: RegExp | string): boolean => {
  if (value && (!params.type || types.indexOf(params.type) !== -1)) {
    return (!params.required && !value) || new RegExp(regexPattern).test(value);
  }

  return true;
};

const isValidOnlyEngAndDigits = (value: string, params: ValidParams): boolean => {
  const types = ['text', 'email', 'password', 'textarea'];

  return isValid(value, types, params, REGEX_CONSTANTS.ONLY_ENG_AND_DIGITS_PATTERN);
};

const isValidOnlyEngLetters = (value: string, params: ValidParams): boolean => {
  const types = ['text'];

  return isValid(value, types, params, REGEX_CONSTANTS.ONLY_ENG_LETTERS_WITH_SPACE_PATTERN);
};

const datetime: DatetimeValidatorType = {
  check: (value, params) => isValidDate(value, params),
  message: keyMap.formsValidationDateTimeError,
};
const int32: Int32ValidatorType = {
  check: (value) => value >= -2147483648 && value <= 2147483647 && Number.isInteger(+value),
  message: keyMap.formsValidationInt32Error,
};

const strength = passwordStrength;

const email: EmailValidatorType = {
  check: (value, params) => isValidEmail(value, params),
  message: keyMap.formsValidationEmailError,
};
const select: SelectValidatorType = {
  check: (value, params) => isValidSelect(value, params),
  message: keyMap.formsValidationRequiredError,
};
const phone: PhoneValidatorType = {
  check: (value, params) => (!params.required && !value ? true : value.toString().replace(/\D+/g, '').length > 6),
  message: keyMap.formsValidationRequiredError,
};
const onlyEngAndDigits: OnlyEngAndDigitsValidatorType = {
  check: (value, params) => isValidOnlyEngAndDigits(value, params),
  message: keyMap.formsValidationOnlyEnglishAndDigitsError,
};
const onlyEngLetters: OnlyEngLettersValidatorType = {
  check: (value, params) => isValidOnlyEngLetters(value, params),
  message: keyMap.formsValidationOnlyEnglishError,
};

class ValidationService {
  validatePinCodeLength = (validationMessage: string, length: number) => {
    return {
      check: (value: FieldValue) => new RegExp(`^[0-9]{${length}}$`).test(value as string),
      message: validationMessage,
    };
  };

  validatePassword = validatePassword;

  isGreaterThan = (amount: number) => {
    return {
      check: (value: FieldValue) => Number(value) > amount,
      message: keyMap.formsValidationValueShouldBeGreaterThan,
      placeholders: { amount: String(amount) },
    };
  };

  isEmptyOrGreaterThan = (amount: number) => {
    return {
      check: (value: FieldValue) => value === null || value === '' || Number(value) > amount,
      message: keyMap.formsValidationValueShouldBeEmptyOrGreaterThan,
      placeholders: { amount: String(amount) },
    };
  };

  isLowerThan = (amount: number) => {
    return {
      check: (value: FieldValue) => Number(value) < amount,
      message: keyMap.formsValidationValueShouldBeLowerThan,
      placeholders: { amount: String(amount) },
    };
  };

  isMaximumStringLength = (max: number) => {
    return {
      check: (value: FieldValue) => !(String(value).length >= max),
      message: keyMap.formValidationLimitValidation,
      placeholders: { limit: String(max) },
    };
  };
}

export const validationService = new ValidationService();

const validators: ValidatorsType = {
  datetime,
  int32,
  strength,
  email,
  select,
  phone,
  onlyEngAndDigits,
  onlyEngLetters,
};

export default validators;
